import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/videa-components';

import type { StatusTypeValue, ZuubManualCheckListPatient } from '@/@types/zuub/contracts';
import { InsuranceStatusCta, StatusType } from '@/@types/zuub/contracts';
import { getVariantStyles } from '@/common-components/elements/StatusChip/helpers';
import {
  getStatusTypeVariant,
} from '@/modules/autoVerify/appointmentsList/AutoVerifyTable';

import type {
  RecentVerificationEntry,
  RecentVerificationStatus,
} from './recentVerificationsCardTypes';
import { isEntryLoading } from './useRunVerification';
import { parseAndFormatDate } from '@/utils/parseDate';

const DOB_PLACEHOLDER = '—';

function mapPatientToEntry(patient: ZuubManualCheckListPatient): RecentVerificationEntry {
  const firstPlan = patient.plans[0];
  const insuranceStatusV2 = firstPlan?.insuranceStatusV2 ?? null;

  let status: RecentVerificationStatus;
  if (isEntryLoading(patient)) {
    status = 'loading';
  } else if (insuranceStatusV2 === InsuranceStatusCta.ACTIVE) {
    status = 'success';
  } else if (insuranceStatusV2 === InsuranceStatusCta.CHECK_MANUALLY) {
    status = 'manual';
  } else {
    status = 'error';
  }

  const dobDisplay = patient.dateOfBirth
    ? (parseAndFormatDate(patient.dateOfBirth, 'MM/dd/yyyy') || DOB_PLACEHOLDER)
    : DOB_PLACEHOLDER;

  return {
    id: patient.unifiedAppointmentId,
    name: patient.patientName,
    dob: dobDisplay,
    status,
    date: patient.lastRun ? format(new Date(patient.lastRun), 'MMM d, yyyy') : '—',
  };
}

export interface RecentVerificationsCardProps {
  patients?: ZuubManualCheckListPatient[];
  isLoading?: boolean;
  isError?: boolean;
  onSelectPatient?: (patient: ZuubManualCheckListPatient) => void;
  selectedPatientId?: string | null;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

export const RecentVerificationsCard = ({
  patients = [],
  isLoading = false,
  isError = false,
  onSelectPatient,
  selectedPatientId,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
}: RecentVerificationsCardProps) => {
  const { t } = useLingui();

  const entries = useMemo(
    () => patients.map(mapPatientToEntry),
    [patients],
  );

  const canSelect = onSelectPatient != null;

  const handleRowClick = (_entry: RecentVerificationEntry, index: number) => {
    const patient = patients[index];
    if (patient && onSelectPatient) {
      onSelectPatient(patient);
    }
  };

  const handleRowKeyDown = (
    e: React.KeyboardEvent,
    entry: RecentVerificationEntry,
    index: number,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRowClick(entry, index);
    }
  };

  return (
    <Card className="vai-flex vai-min-h-0 vai-min-w-0 vai-max-h-[680px] vai-flex-1 vai-flex-col vai-overflow-hidden">
      <CardHeader className="vai-flex-shrink-0">
        <CardTitle>
          <Trans>Recent Verifications</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent className="vai-flex-1 vai-min-h-0 vai-overflow-y-auto vai-pt-0 vai-overflow-x-hidden">
        {isLoading && (
          <div className="vai-flex vai-items-center vai-justify-center vai-py-8">
            <FontAwesomeIcon
              icon={faCircleNotch}
              className="vai-h-8 vai-w-8 vai-text-gray-400 vai-animate-spin"
              aria-hidden
            />
          </div>
        )}
        {isError && (
          <p className="vai-py-4 vai-text-sm vai-text-red-600">
            {t`Failed to load recent verifications.`}
          </p>
        )}
        {!isLoading && !isError && entries.length === 0 && (
          <p className="vai-py-4 vai-text-sm vai-text-muted-foreground">
            <Trans>No recent verifications.</Trans>
          </p>
        )}
        {!isLoading && !isError && entries.length > 0 && (
          <ul className="vai-space-y-3">
            {entries.map((entry, index) => {
              const patient = patients[index];
              const isSelected =
                selectedPatientId != null && entry.id === selectedPatientId;
              return (
                <li
                  key={entry.id}
                  role="button"
                  tabIndex={canSelect ? 0 : undefined}
                  onClick={() => canSelect && handleRowClick(entry, index)}
                  onKeyDown={(e) =>
                    canSelect && handleRowKeyDown(e, entry, index)
                  }
                  className={`vai-flex vai-items-center vai-gap-3 vai-rounded-md vai-border vai-p-3 vai-shadow-sm ${
                    canSelect
                      ? 'vai-cursor-pointer vai-border-gray-100 vai-bg-white hover:vai-bg-gray-50'
                      : 'vai-border-gray-100 vai-bg-white'
                  } ${isSelected ? 'vai-border-primary vai-ring-1 vai-ring-primary' : ''}`}
                >
                  <div className="vai-min-w-0">
                    <p className="vai-font-medium vai-text-gray-900">{entry.name}</p>
                    <p className="vai-text-sm vai-text-gray-500">
                      <Trans>DOB</Trans>: {entry.dob}
                    </p>
                  </div>
                  <div className="vai-ml-auto vai-flex vai-items-center vai-gap-2 vai-flex-shrink-0">
                    {entry.status === 'loading' ? (
                      <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="vai-h-4 vai-w-4 vai-text-blue-500 vai-animate-spin"
                        aria-hidden
                      />
                    ) : (
                      (() => {
                        const variant = getStatusTypeVariant(
                          (patient?.plans[0]?.statusType ?? StatusType.ACTION) as StatusTypeValue,
                        );
                        const styles = getVariantStyles(variant);
                        return (
                          <FontAwesomeIcon
                            icon={styles.icon}
                            className={`vai-h-4 vai-w-4 ${styles.iconColor}`}
                            aria-hidden
                          />
                        );
                      })()
                    )}
                    <span className="vai-text-sm vai-text-gray-500">
                      {entry.date}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      {totalPages > 1 && (
        <div className="vai-flex vai-items-center vai-justify-between vai-border-t vai-border-gray-200 vai-px-4 vai-py-2 vai-flex-shrink-0">
          <button
            type="button"
            onClick={() => onPageChange?.(currentPage - 1)}
            disabled={currentPage <= 1}
            className="vai-text-sm vai-text-blue-600 disabled:vai-text-gray-300 disabled:vai-cursor-not-allowed"
          >
            <Trans>Previous</Trans>
          </button>
          <span className="vai-text-xs vai-text-muted-foreground">
            <Trans>Page {currentPage} of {totalPages}</Trans>
          </span>
          <button
            type="button"
            onClick={() => onPageChange?.(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="vai-text-sm vai-text-blue-600 disabled:vai-text-gray-300 disabled:vai-cursor-not-allowed"
          >
            <Trans>Next</Trans>
          </button>
        </div>
      )}
    </Card>
  );
};
